import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  type VirtualElement,
} from '@floating-ui/react';
import {
  type GeoProjection,
  geoMercator,
  geoNaturalEarth1,
  geoPath,
} from 'd3-geo';
import { select } from 'd3-selection';
import { zoom as d3Zoom, type ZoomBehavior, zoomIdentity } from 'd3-zoom';
import { toBlob, toPng } from 'html-to-image';
import { Check, Copy, Download, RotateCcw, Share2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { feature, mesh } from 'topojson-client';
import type { GeometryCollection, Topology } from 'topojson-specification';
import type { RatingMode } from '@/components/tournament';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  atlasNameToCode,
  countryDetails,
  getRatingColor,
  type Rating,
  ratingLabels,
} from '@/data/countries';
import { SVG_PATH_DEFAULTS } from '@/lib/geo-utils';
import { loadWorldData } from '@/lib/map-data';
import { loadTournamentState, rankingsToRatings } from '@/lib/tournament';

interface WorldMapProps {
  ratings: Record<string, number>;
  scopeCountryCodes?: string[];
  scopeId?: string;
  mode?: RatingMode;
  scopeName?: string;
}

export function WorldMap({
  ratings: passedRatings,
  scopeCountryCodes,
  scopeId,
  mode = 'list',
  scopeName,
}: WorldMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const exportContainerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<{
    name: string;
    code: string;
    rating?: number;
  } | null>(null);
  const [virtualElement, setVirtualElement] = useState<VirtualElement | null>(
    null,
  );
  const hoverBorderRef = useRef<any>(null);

  // Share state
  const [caption, setCaption] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Calculate ratings based on mode
  const ratings = useMemo(() => {
    if (mode === 'tournament' && scopeId) {
      // Try to load tournament rankings for this scope
      const tournamentState = loadTournamentState(scopeId);
      if (tournamentState?.finalRankings) {
        // Use tournament rankings converted to ratings
        return rankingsToRatings(tournamentState.finalRankings);
      }
    }
    // Fall back to passed ratings (list mode or no tournament data)
    return passedRatings;
  }, [mode, scopeId, passedRatings]);

  // Reset zoom handler
  const handleResetZoom = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = select(svgRef.current);
    svg
      .transition()
      .duration(300)
      .call(zoomRef.current.transform as any, zoomIdentity);
    setZoomLevel(1);
  }, []);

  // Share as image handler
  const handleShare = useCallback(
    async (action: 'download' | 'copy') => {
      if (!exportContainerRef.current) return;

      setIsExporting(true);
      try {
        // Hide tooltip during export
        const tooltip =
          exportContainerRef.current.querySelector('[data-tooltip]');
        if (tooltip) (tooltip as HTMLElement).style.display = 'none';

        if (action === 'download') {
          const dataUrl = await toPng(exportContainerRef.current, {
            backgroundColor: '#ffffff',
            pixelRatio: 2,
          });
          const link = document.createElement('a');
          link.download = `where-in-the-world-${scopeName || 'map'}.png`;
          link.href = dataUrl;
          link.click();
          toast.success('Map downloaded!');
        } else {
          const blob = await toBlob(exportContainerRef.current, {
            backgroundColor: '#ffffff',
            pixelRatio: 2,
          });
          if (blob) {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob }),
            ]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success('Map copied to clipboard!');
          }
        }

        // Restore tooltip
        if (tooltip) (tooltip as HTMLElement).style.display = '';
      } catch (error) {
        console.error('Failed to export map:', error);
        toast.error('Failed to export map');
      } finally {
        setIsExporting(false);
      }
    },
    [scopeName],
  );

  const { refs, floatingStyles } = useFloating({
    elements: {
      reference: virtualElement as any,
    },
    placement: 'right',
    middleware: [offset(10), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (!svgRef.current) return;

    const width = 1200;
    const height = 600;

    select(svgRef.current).selectAll('*').remove();

    const svg = select(svgRef.current)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto')
      .style('touch-action', 'none');

    const g = svg.append('g');

    // Add zoom behavior with touch support
    const zoom = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 20])
      .translateExtent([
        [-100, -100],
        [width + 100, height + 100],
      ])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        setZoomLevel(event.transform.k);
      });

    zoomRef.current = zoom;
    svg.call(zoom);

    // Load both resolutions in parallel: 10m for rated countries (crisp), 50m for background
    Promise.all([loadWorldData('10m'), loadWorldData('50m')])
      .then(([world10m, world50m]) => {
        if (!world50m) throw new Error('No data received');

        // Use 50m for projection calculation and base layer (faster)
        const features50m = feature(
          world50m,
          world50m.objects.countries,
        ) as any;
        // Use 10m for rated countries overlay (crisp detail)
        const features10m = world10m
          ? (feature(world10m, world10m.objects.countries) as any)
          : null;

        // Determine projection based on scope
        let projection: GeoProjection;

        // Check if we have a regional scope (not full world)
        const isRegionalScope =
          scopeCountryCodes && scopeCountryCodes.length < 150;

        if (isRegionalScope) {
          // Filter features to only those in scope
          const scopeFeatures = features50m.features.filter((f: any) => {
            const countryCode = atlasNameToCode[f.properties.name];
            return countryCode && scopeCountryCodes.includes(countryCode);
          });

          if (scopeFeatures.length > 0) {
            // Create a combined feature collection for fitting
            const scopeCollection = {
              type: 'FeatureCollection',
              features: scopeFeatures,
            };

            // Use Mercator for regional views (better shape preservation)
            projection = geoMercator().fitExtent(
              [
                [width * 0.05, height * 0.05],
                [width * 0.95, height * 0.95],
              ],
              scopeCollection as any,
            );
          } else {
            // Fallback to world view
            projection = geoNaturalEarth1()
              .scale(width / 5)
              .translate([width / 2, height / 2]);
          }
        } else {
          // Full world view
          projection = geoNaturalEarth1()
            .scale(width / 5)
            .translate([width / 2, height / 2]);
        }

        const path = geoPath().projection(projection);

        // Layer 1: Render ALL countries from 50m data (base layer with fills)
        g.append('g')
          .attr('class', 'countries-base')
          .selectAll('path')
          .data(features50m.features)
          .join('path')
          .attr('d', path as any)
          .attr('fill', (d: any) => {
            const countryName = d.properties.name;
            const countryCode = atlasNameToCode[countryName];

            // If scope is defined, dim countries outside the scope
            const isInScope =
              !scopeCountryCodes || scopeCountryCodes.includes(countryCode);

            if (!isInScope) {
              return '#e8e4df'; // Warm gray for out-of-scope countries
            }

            const rating = countryCode ? ratings[countryCode] : undefined;
            return getRatingColor(rating);
          })
          .attr('stroke', 'none')
          .attr('class', 'transition-colors duration-200 cursor-pointer')
          .on('mouseenter', (event, d: any) => {
            const countryName = d.properties.name;
            const countryCode = atlasNameToCode[countryName];
            const rating = countryCode ? ratings[countryCode] : undefined;

            // Update hover border
            if (hoverBorderRef.current) {
              hoverBorderRef.current
                .datum(d)
                .attr('d', path as any)
                .attr('stroke', '#000')
                .attr('stroke-width', 1.5)
                .attr('vector-effect', 'non-scaling-stroke');
            }

            // Create virtual element for floating-ui
            const rect = {
              x: event.clientX,
              y: event.clientY,
              width: 0,
              height: 0,
              top: event.clientY,
              right: event.clientX,
              bottom: event.clientY,
              left: event.clientX,
            };

            setVirtualElement({
              getBoundingClientRect: () => rect as DOMRect,
            });

            setHoveredCountry({ name: countryName, code: countryCode, rating });
          })
          .on('mousemove', (event) => {
            const rect = {
              x: event.clientX,
              y: event.clientY,
              width: 0,
              height: 0,
              top: event.clientY,
              right: event.clientX,
              bottom: event.clientY,
              left: event.clientX,
            };

            setVirtualElement({
              getBoundingClientRect: () => rect as DOMRect,
            });
          })
          .on('mouseleave', () => {
            if (hoverBorderRef.current) {
              hoverBorderRef.current.attr('stroke', 'none');
            }

            setHoveredCountry(null);
            setVirtualElement(null);
          });

        // Layer 2: Overlay rated countries from 10m data (crisp detail)
        if (features10m) {
          // Filter to only countries with ratings (in play)
          const ratedFeatures = features10m.features.filter((f: any) => {
            const countryCode = atlasNameToCode[f.properties.name];
            return countryCode && ratings[countryCode] !== undefined;
          });

          if (ratedFeatures.length > 0) {
            g.append('g')
              .attr('class', 'countries-rated-overlay')
              .selectAll('path')
              .data(ratedFeatures)
              .join('path')
              .attr('d', path as any)
              .attr('fill', (d: any) => {
                const countryName = d.properties.name;
                const countryCode = atlasNameToCode[countryName];
                const rating = countryCode ? ratings[countryCode] : undefined;
                return getRatingColor(rating);
              })
              .attr('stroke', 'none')
              .style('pointer-events', 'none'); // Let base layer handle interactions
          }
        }

        // Layer 3: Render country borders as a separate mesh layer (from 50m - simpler)
        const borders = mesh(
          world50m,
          world50m.objects.countries as GeometryCollection,
          (a, b) => a !== b,
        );

        g.append('path')
          .datum(borders)
          .attr('d', path as any)
          .attr('fill', 'none')
          .attr('stroke', '#a8a198') // Visible warm gray border
          .attr('stroke-width', 0.5)
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('vector-effect', 'non-scaling-stroke')
          .style('pointer-events', 'none');

        // Layer 4: Add hover border layer (topmost)
        hoverBorderRef.current = g
          .append('path')
          .attr('fill', 'none')
          .attr('stroke', 'none')
          .attr('stroke-width', 1.5)
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('vector-effect', 'non-scaling-stroke')
          .style('pointer-events', 'none');
      })
      .catch((error) => {
        console.error('Failed to load map data:', error);
        svg
          .append('text')
          .attr('x', width / 2)
          .attr('y', height / 2)
          .attr('text-anchor', 'middle')
          .attr('class', 'text-muted-foreground text-sm')
          .text('Unable to load map. Please refresh the page.');
      });
  }, [ratings, scopeCountryCodes]);

  return (
    <div className="relative" ref={containerRef}>
      {/* Export container - this entire section gets captured as image */}
      <div
        ref={exportContainerRef}
        className="bg-white rounded-xl overflow-hidden"
      >
        {/* Caption (visible when set) */}
        {caption && (
          <div className="px-4 py-3 bg-gradient-to-r from-primary/10 to-primary/5 border-b border-border">
            <p className="text-sm font-medium text-foreground">{caption}</p>
          </div>
        )}

        {/* Map */}
        <div className="w-full bg-stone-100 border border-stone-200 overflow-hidden rounded-lg">
          <svg ref={svgRef} className="w-full h-auto min-h-[400px]" />
        </div>

        {/* Watermark for export */}
        <div className="px-4 py-2 bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
          <span>Country Tier</span>
          <span>countrytier.com</span>
        </div>
      </div>

      {/* Controls bar */}
      <div className="mt-3 flex flex-wrap items-center gap-3 justify-between">
        {/* Zoom controls */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-mono">
            Zoom: {zoomLevel.toFixed(1)}x
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetZoom}
            disabled={zoomLevel === 1}
            className="h-8 text-xs"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Reset
          </Button>
        </div>

        {/* Instructions */}
        <span className="text-xs text-muted-foreground hidden sm:block">
          Scroll to zoom • Drag to pan
        </span>

        {/* Share controls */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleShare('download')}
            disabled={isExporting}
            className="h-8 text-xs"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleShare('copy')}
            disabled={isExporting}
            className="h-8 text-xs"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 mr-1.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5 mr-1.5" />
            )}
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </div>

      {/* Caption input */}
      <div className="mt-3">
        <div className="flex items-center gap-2">
          <Share2 className="w-4 h-4 text-muted-foreground shrink-0" />
          <Input
            placeholder="Add a caption for your map..."
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Tooltip */}
      {hoveredCountry && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          data-tooltip
          className="pointer-events-none z-50 p-0 rounded-lg shadow-xl border-2 border-border backdrop-blur-md bg-card/95 overflow-hidden max-w-[240px]"
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-border/50 bg-muted/20">
            <div className="font-display text-base font-semibold">
              {hoveredCountry.name}
            </div>
            {hoveredCountry.rating !== undefined ? (
              <div className="flex items-center gap-1.5 mt-1">
                <div
                  className="w-2.5 h-2.5 rounded-full border border-white/20 shadow-sm"
                  style={{
                    backgroundColor: getRatingColor(hoveredCountry.rating),
                  }}
                />
                <span className="text-xs font-semibold text-muted-foreground">
                  {ratingLabels[hoveredCountry.rating as Rating]?.label ??
                    'Unknown'}
                </span>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mt-1">
                Not rated
              </div>
            )}
          </div>

          {/* Details Body */}
          {hoveredCountry.code && countryDetails[hoveredCountry.code] && (
            <div className="p-3 text-xs space-y-1.5">
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Capital</span>
                <span className="font-medium">
                  {countryDetails[hoveredCountry.code].capital}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Population</span>
                <span className="font-medium">
                  {countryDetails[hoveredCountry.code].population}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Currency</span>
                <span className="font-medium">
                  {countryDetails[hoveredCountry.code].currency}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
